/**
 * GlobalSearch Component - Search bar with dropdown suggestions
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { searchAPI, GlobalSearchResult } from '@/api/searchAPI';
import { useAppSelector } from '@/store/hooks';
import { TaskTypeIcon } from './TaskTypeIcon';

const MIN_QUERY_LENGTH = 1;
const DEFAULT_TASK_LIMIT = 20;
const VIEW_ALL_TASK_LIMIT = 500;

export const GlobalSearch = () => {
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<GlobalSearchResult>({ projects: [], tasks: [], subtasks: [], taskTotal: 0 });
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimerRef = useRef<number>();

    // Flatten results for keyboard navigation
    const flatResults = [
        ...results.projects.map(p => ({ type: 'project' as const, data: p })),
        ...results.tasks.map(t => ({ type: 'task' as const, data: t })),
        ...results.subtasks.map(s => ({ type: 'subtask' as const, data: s }))
    ];

    // Debounced search
    const performSearch = useCallback(async (searchQuery: string, opts?: { taskLimit?: number; keepResults?: boolean }) => {
        const trimmed = searchQuery.trim();
        if (trimmed.length < MIN_QUERY_LENGTH) {
            setResults({ projects: [], tasks: [], subtasks: [], taskTotal: 0 });
            setIsLoading(false);
            setIsLoadingMore(false);
            setIsOpen(false);
            return;
        }

        const keepExisting = !!opts?.keepResults;
        if (keepExisting) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }
        try {
            const searchResults = await searchAPI.globalSearch(trimmed, {
                userId: user?.id,
                taskLimit: opts?.taskLimit ?? DEFAULT_TASK_LIMIT,
                organization_id:user?.organization_id
            });
            setResults(searchResults);
            setIsOpen(true);
            setSelectedIndex(-1);
        } catch (error) {
            console.error('Search error:', error);
            setResults({ projects: [], tasks: [], subtasks: [], taskTotal: 0 });
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [user?.id]);

    // Handle input change with debouncing
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = window.setTimeout(() => {
            performSearch(value);
        }, 250);
    };

    const handleViewAll = () => {
        if (!query.trim()) return;
        performSearch(query, { taskLimit: VIEW_ALL_TASK_LIMIT, keepResults: true });
    };

    // Navigate to result
    const navigateToResult = (item: typeof flatResults[0]) => {
        if (item.type === 'project') {
            navigate(`/projects/${item.data.id}/board`);
        } else if (item.type === 'task') {
            navigate(`/task/${item.data.id}`);
        } else if (item.type === 'subtask') {
            navigate(`/subtask/${item.data.id}`);
        }
        setQuery('');
        setIsOpen(false);
        setResults({ projects: [], tasks: [], subtasks: [], taskTotal: 0 });
        inputRef.current?.blur();
    };

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen || flatResults.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => (prev < flatResults.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
                    navigateToResult(flatResults[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setQuery('');
                inputRef.current?.blur();
                break;
        }
    };

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const hasResults = results.projects.length > 0 || results.tasks.length > 0 || results.subtasks.length > 0;
    const hasMoreTasks = results.taskTotal > results.tasks.length && results.tasks.length < VIEW_ALL_TASK_LIMIT;
    const isInitialLoading = isLoading && !hasResults;
    const showDropdown = isOpen && (isLoading || isLoadingMore || hasResults || (query.trim().length >= MIN_QUERY_LENGTH && !isLoading && !hasResults));

    return (
        <div className="w-full max-w-2xl relative" ref={searchRef}>
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
                ref={inputRef}
                className="w-full h-8 pl-12 pr-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm placeholder-gray-400"
                placeholder="Search projects, tasks, subtasks..."
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => query.length >= 2 && setIsOpen(true)}
            />

            {/* Dropdown Results */}
            {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
                    {isInitialLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            <span className="ml-2 text-sm text-gray-500">Searching...</span>
                        </div>
                    ) : hasResults ? (
                        <div className="py-2">
                            {/* Projects Section */}
                            {results.projects.length > 0 && (
                                <div className="mb-2">
                                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Projects</div>
                                    {results.projects.map((project, idx) => {
                                        const globalIdx = idx;
                                        return (
                                            <button
                                                key={project.id}
                                                onClick={() => navigateToResult({ type: 'project', data: project })}
                                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3 ${selectedIndex === globalIdx ? 'bg-blue-50' : ''
                                                    }`}
                                            >
                                                <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                                                    {project.key?.substring(0, 2) || 'PR'}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{project.name}</div>
                                                    <div className="text-xs text-gray-500 truncate">{project.key}</div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Tasks Section */}
                            {results.tasks.length > 0 && (
                                <div className="mb-2">
                                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Tasks</div>
                                    {results.tasks.map((task, idx) => {
                                        const globalIdx = results.projects.length + idx;
                                        return (
                                            <button
                                                key={task.id}
                                                onClick={() => navigateToResult({ type: 'task', data: task })}
                                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3 ${selectedIndex === globalIdx ? 'bg-blue-50' : ''
                                                    }`}
                                            >
                                                <TaskTypeIcon
                                                    url={(task as any).task_type_icon}
                                                    name={(task as any).task_type_name || 'Task'}
                                                    className="w-5 h-5 flex-shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{task.title}</div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        {task.id} • {(task as any).project_key || 'Project'}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                    {hasMoreTasks && (
                                        <div className="px-3 py-2">
                                            <button
                                                onClick={handleViewAll}
                                                disabled={isLoadingMore}
                                                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                                            >
                                                View all results
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Subtasks Section */}
                            {results.subtasks.length > 0 && (
                                <div>
                                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Subtasks</div>
                                    {results.subtasks.map((subtask, idx) => {
                                        const globalIdx = results.projects.length + results.tasks.length + idx;
                                        return (
                                            <button
                                                key={subtask.id}
                                                onClick={() => navigateToResult({ type: 'subtask', data: subtask })}
                                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3 ${selectedIndex === globalIdx ? 'bg-blue-50' : ''
                                                    }`}
                                            >
                                                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                                    <div className="w-3 h-3 border-2 border-gray-400 rounded-sm" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-900 truncate">{subtask.title}</div>
                                                    <div className="text-xs text-gray-500 truncate">
                                                        Subtask • {(subtask as any).parent_task_id || 'Task'}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Loading more indicator */}
                            {isLoadingMore && (
                                <div className="flex items-center justify-center py-3">
                                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                                    <span className="ml-2 text-sm text-gray-500">Loading more…</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="py-8 text-center text-sm text-gray-500">
                            No results found for "{query}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
